import axios from 'axios';
import { clearAuthTokens, getAccessToken, getRefreshToken } from '@/shared/session/auth';
import { externalConnections, LOGOUT_REDIRECT_URI_KEY } from '@/constants/Constants';
import { buildCurrentPageUrl } from '@/shared/utils/url.util';
import { logger } from '@/shared/libs/logger';

type LogoutReason = 'user_initiated' | 'session_expired' | 'auth_error';

// Caps the revoke call so a slow login API can't hold the user on the loader.
const REVOKE_TIMEOUT_MS = 5_000;

let logoutStarted = false;

export function hasLogoutStarted(): boolean {
  return logoutStarted;
}

export async function logout({
  reason,
  context,
}: {
  reason: LogoutReason;
  /** Extra telemetry on why the session ended. */
  context?: Record<string, unknown>;
}) {
  // Parallel failing requests and the expiry check can all land here; only the first redirects.
  if (logoutStarted) {
    return;
  }
  logoutStarted = true;

  // Resolved before anything is awaited: the app can still navigate while the
  // revoke call is in flight, and the user should come back to where they were.
  const redirectTarget =
    reason === 'user_initiated'
      ? externalConnections.logout
      : buildLogoutUrl(buildCurrentPageUrl());

  logger.info('Session ended', { reason, ...context });

  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  clearAuthTokens();

  // /v1/logout requires both; skip when either is missing to avoid a guaranteed 401.
  if (accessToken && refreshToken) {
    try {
      await axios.post(
        externalConnections.loginApiLogout,
        { refresh_token: refreshToken },
        { headers: { Authorization: `Bearer ${accessToken}` }, timeout: REVOKE_TIMEOUT_MS },
      );
    } catch (error) {
      logger.error('Error revoking token', undefined, error);
    }
  }

  globalThis.location.replace(redirectTarget);
}

export function buildLogoutUrl(redirectUri: string | null): string {
  const logoutUrlString = externalConnections.logout;

  if (redirectUri !== null && URL.canParse(redirectUri) && URL.canParse(logoutUrlString)) {
    const logoutUrl = new URL(logoutUrlString);
    logoutUrl.searchParams.set(LOGOUT_REDIRECT_URI_KEY, redirectUri);
    return logoutUrl.toString();
  }

  return logoutUrlString;
}

export default logout;
